import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Drawer, Flex, Form, Input, message, Select, Table, TableProps, Tag } from 'antd';
import copy from 'copy-to-clipboard';
import { IKeys, IUserRoles, IWorker } from 'models';
import { useState } from 'react';
import { userService } from 'services';
import { userStore } from 'stores';

const PER_PAGE = 12;

export const Users = () => {
	const [page, setPage] = useState(1);
	const [drawer, openDrawer] = useState(false);
	const [messageApi, contextHolder] = message.useMessage();
	const [form] = Form.useForm();
	const { isLoading, data, refetch } = useQuery({
		queryKey: [IKeys.USERS, { PER_PAGE, page }],
		queryFn: () => userService.getAll({
			per_page: PER_PAGE,
			page
		}),
		enabled: userStore.isAuth
	});
	const updateUser = useMutation({
		mutationFn: userService.update,
		onSuccess: (data) => {
			userStore.updateUser(data);
			messageApi.success('User updated successfully');
		}
	});
	const createUser = useMutation({
		mutationFn: userService.create,
		onSuccess: (data) => {
			userStore.addUser(data);
			messageApi.success('User created successfully');
		}
	});

	const deactivateUser = useMutation({
		mutationFn: userService.remove,
		onSuccess: (data) => {
			userStore.removeUser(data.id);
			messageApi.success('User deactivated successfully');
			refetch();
			handleClose();
		}
	});

	const handleFinish = async (values: IWorker) => {
		values.id ? await updateUser.mutateAsync(values) :  await createUser.mutateAsync(values);
		refetch();
		openDrawer(false);
	};
	const handleClose = () => {
		form.resetFields();
		openDrawer(false);
	};

	const handleDeactivate = () => {
		const id = form.getFieldValue('id');
		id && deactivateUser.mutate(id);
	};

	const columns: TableProps<IWorker>['columns'] = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			render: text => <Tag color="blue" onClick={(e) => {
				e.stopPropagation();
				copy(`https://smartpeoplemoving.com/book?worker=${text}`);
				messageApi.info('Copied!');
			}}>{text}</Tag>
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name'
		},
		{
			title: 'Phone',
			dataIndex: ['acf', 'phone'],
			key: 'phone',
			render: text => <a href={`tel:${text}`} onClick={(e) => e.stopPropagation()}>{text}</a>
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email'
		},
		{
			title: 'Role',
			dataIndex: 'roles',
			key: 'role',
			render: text => text[0]
		}
	];

	return (
		<>
			<Table<IWorker>
				rowKey={(el) => el.id}
				columns={columns}
				loading={isLoading}
				dataSource={data?.data}
				size="small"
				onRow={(data) => ({
					onClick: () => {
						form.setFieldsValue(data);
						openDrawer(true);
					}
				})}
				pagination={{
					pageSize: PER_PAGE,
					current: page,
					total: data?.headers['x-wp-total'],
					onChange: (val) => setPage(val)
				}}
			/>
			<Button block type="primary" onClick={() => openDrawer(true)}>Create a user</Button>
			<Drawer
				open={drawer}
				destroyOnHidden
				onClose={handleClose}
			>
				<Form<IWorker>
					form={form}
					layout="vertical"
					onFinish={handleFinish}
				>
					<Form.Item
						name="id"
						hidden
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="name"
						label="Name"
						rules={[{ required: true, message: 'Name is required' }]}
					>
						<Input />
					</Form.Item>

					<Form.Item
						name="email"
						label="Email"
						rules={[
							{ required: true, message: 'Email is required' },
							{ type: 'email', message: 'Enter a valid email' }
						]}
					>
						<Input />
					</Form.Item>

					<Form.Item
						name={['acf', 'phone']}
						label="Phone"
						rules={[{ required: true, message: 'Phone is required' }]}
					>
						<Input />
					</Form.Item>

					<Form.Item
						name="roles"
						label="Roles"
						rules={[{ required: true, message: 'Select at least one role' }]}
					>
						<Select 
							mode="multiple"
							placeholder="Select role"
							onChange={(value) => {
								form.setFieldsValue({ roles: value.slice(-1) });
							}}
							options={Object.values(IUserRoles).map(el => ({ value: el, label: el }))} />
					</Form.Item>
					<Form.Item
						name="password"
						label="Password"
						rules={
							!form.getFieldValue('id')
								? [
									{ required: true, message: 'Password is required when creating a user' },
									{ min: 6, message: 'Password must be at least 6 characters' }
								]
								: [
									{ min: 6, message: 'Password must be at least 6 characters' }
								]
						}
					>
						<Input.Password placeholder="Enter password" />
					</Form.Item>

					<Form.Item>
						<Flex gap={8}>
							<Button onClick={handleClose}>Cancel</Button>
							<Button 
								loading={deactivateUser.isPending}
								danger
								onClick={handleDeactivate}
							>
								Deactivate
							</Button>
							<Button type="primary" htmlType="submit">
                Save
							</Button>
						</Flex>
					</Form.Item>
				</Form>
			</Drawer>
			{contextHolder}
		</>
	);
};